import React from 'react';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import loadable from '@loadable/component';

import { FormattedMessage } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { SELLER_USER_TYPE } from '../../config/userRoles';
import { COMPANY, VERIFICATION_URL } from '../../content/legal';
import { ExternalLink, NamedLink } from '../../components';

import css from './FooterContainer.module.css';

const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

// Call-to-action that demotes the Verified Seller sign-up out of the header and
// into the footer. It keeps the existing seller (provider) sign-up flow intact —
// it just links straight into it via the Verified-Seller user type.
//
// Both buttons are pinned to that user type: the sign-up goes to the seller
// sign-up form rather than the generic "who are you?" chooser, and the log in
// goes to the seller-scoped login, whose own "Sign up" tab stays on the seller
// flow. Neither can drop a vendor into the shopper journey by accident.
const SellerCta = () => {
  return (
    <section className={css.sellerCta}>
      <div className={css.sellerCtaInner}>
        <div className={css.sellerCtaText}>
          <h2 className={css.sellerCtaHeading}>
            <FormattedMessage id="FooterContainer.sellerCtaHeading" />
          </h2>
          <p className={css.sellerCtaSubheading}>
            <FormattedMessage id="FooterContainer.sellerCtaSubheading" />
          </p>
        </div>
        <div className={css.sellerCtaActions}>
          <NamedLink
            name="SignupForUserTypePage"
            params={{ userType: SELLER_USER_TYPE }}
            className={css.sellerCtaButton}
          >
            <FormattedMessage id="FooterContainer.sellerCtaButton" />
          </NamedLink>
          <NamedLink
            name="LoginForUserTypePage"
            params={{ userType: SELLER_USER_TYPE }}
            className={css.sellerCtaButtonSecondary}
          >
            <FormattedMessage id="FooterContainer.sellerCtaLoginButton" />
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

// ethy's own footer copy and social profiles.
//
// The footer asset is edited in Console (Content → Footer), which ships with
// placeholder text ("...add your slogan here") and whatever social links happen
// to be configured. These constants are the values ethy actually wants, and they
// are applied over the asset below so the live footer is correct regardless of
// what the hosted asset currently says.
const SLOGAN =
  'Independent brands with sustainability claims checked against evidence, so you can see what sits behind each claim.';

const COPYRIGHT_HOLDER = 'Scratch the Surface ltd.';

const SOCIAL_PROFILES = [
  { platform: 'instagram', url: 'https://www.instagram.com/ethy.uk/' },
  { platform: 'facebook', url: 'https://www.facebook.com/ethy.uk' },
];

const socialMediaLinks = SOCIAL_PROFILES.map(({ platform, url }) => ({
  blockType: 'socialMediaLink',
  blockName: platform,
  link: { fieldType: 'socialMediaLink', platform, url },
}));

// The footer navigation.
//
// The Console footer asset lets an operator paste a markdown list of links into
// a single column, which is how the footer ended up with its links strung out
// down the right-hand edge. We define the navigation here instead: grouped into
// three labelled columns that the footer grid lays out evenly, in the same
// order on every breakpoint.
//
// A link is either
// - internal: `route` (+ optional `params`), resolved through the route
//   configuration so the marketplace path prefix is applied; or
// - external: `href`.
//
// `newTab` opens the link in a new tab. The legal documents use it so that a
// shopper reading the terms mid-checkout doesn't lose their place.
const FOOTER_NAV = [
  {
    id: 'shop',
    headingId: 'FooterContainer.navShopHeading',
    links: [
      { id: 'products', labelId: 'FooterContainer.navAllProducts', route: 'SearchPage' },
      { id: 'basket', labelId: 'FooterContainer.navBasket', route: 'CartPage' },
    ],
  },
  {
    id: 'about',
    headingId: 'FooterContainer.navAboutHeading',
    links: [
      { id: 'verification', labelId: 'FooterContainer.navVerification', href: VERIFICATION_URL },
      { id: 'ethy', labelId: 'FooterContainer.navEthySite', href: COMPANY.website },
      {
        id: 'sell',
        labelId: 'FooterContainer.navSell',
        route: 'SignupForUserTypePage',
        params: { userType: SELLER_USER_TYPE },
      },
    ],
  },
  {
    id: 'legal',
    headingId: 'FooterContainer.navLegalHeading',
    links: [
      {
        id: 'terms-of-service',
        labelId: 'FooterContainer.navTermsOfService',
        route: 'TermsOfServicePage',
        newTab: true,
      },
      {
        id: 'terms-of-sale',
        labelId: 'FooterContainer.navTermsOfSale',
        route: 'TermsAndConditionsOfSalePage',
        newTab: true,
      },
      {
        id: 'privacy-policy',
        labelId: 'FooterContainer.navPrivacyPolicy',
        route: 'PrivacyPolicyPage',
        newTab: true,
      },
    ],
  },
];

// A single footer link. In-app links stay in the tab unless they ask for a new
// one; external links always open in a new tab (that's ExternalLink's default).
const FooterNavLink = props => {
  const { link } = props;
  const routeConfiguration = useRouteConfiguration();
  const { labelId, route, params, href, newTab } = link;
  const label = <FormattedMessage id={labelId} />;

  if (route && !newTab) {
    return (
      <NamedLink className={css.footerNavLink} name={route} params={params}>
        {label}
      </NamedLink>
    );
  }

  const url = route ? pathByRouteName(route, routeConfiguration, params) : href;

  return (
    <ExternalLink className={css.footerNavLink} href={url}>
      {label}
    </ExternalLink>
  );
};

// One labelled column of footer links. Rendered by the footer section through
// the block mapping below, so that it lands inside the footer's own grid.
const FooterNavColumn = props => {
  const { column } = props;

  return (
    <nav className={css.footerNav} aria-labelledby={`footer-nav-${column.id}`}>
      <h2 className={css.footerNavHeading} id={`footer-nav-${column.id}`}>
        <FormattedMessage id={column.headingId} />
      </h2>
      <ul className={css.footerNavList}>
        {column.links.map(link => (
          <li key={link.id} className={css.footerNavItem}>
            <FooterNavLink link={link} />
          </li>
        ))}
      </ul>
    </nav>
  );
};

const FOOTER_NAV_BLOCK_TYPE = 'ethyFooterNav';

const footerNavBlocks = FOOTER_NAV.map(column => ({
  blockType: FOOTER_NAV_BLOCK_TYPE,
  blockId: `footer-nav-${column.id}`,
  column,
}));

const footerSectionOptions = {
  blockComponents: { [FOOTER_NAV_BLOCK_TYPE]: { component: FooterNavColumn } },
};

const FooterComponent = () => {
  const { footer = {}, topbar } = useConfiguration();

  // If footer asset is not set, let's not render Footer at all.
  if (Object.keys(footer).length === 0) {
    return <SellerCta />;
  }

  // The footer asset does not specify sectionId or sectionType. However, the SectionBuilder
  // expects sectionId and sectionType in order to identify the section. We add those
  // attributes here before passing the asset to SectionBuilder.
  const footerSection = {
    ...footer,
    slogan: { fieldType: 'markdown', content: SLOGAN },
    copyright: {
      fieldType: 'markdown',
      content: `© ${new Date().getFullYear()} ${COPYRIGHT_HOLDER} All rights reserved.`,
    },
    socialMediaLinks,
    // Our own navigation replaces whatever links the asset carries, and the
    // column count tells the footer grid to lay them out three abreast next to
    // the brand column instead of stacking them in a single narrow column.
    blocks: footerNavBlocks,
    numberOfColumns: FOOTER_NAV.length,
    sectionId: 'footer',
    sectionType: 'footer',
    linkLogoToExternalSite: topbar?.logoLink,
  };

  return (
    <>
      <SellerCta />
      <SectionBuilder sections={[footerSection]} options={footerSectionOptions} />
    </>
  );
};

// NOTE: if you want to add dynamic data to FooterComponent,
//       you could just connect this FooterContainer to Redux Store
//
// const mapStateToProps = state => {
//   const { currentUser } = state.user;
//   return { currentUser };
// };
// const FooterContainer = compose(connect(mapStateToProps))(FooterComponent);
// export default FooterContainer;

export default FooterComponent;
